import React, { PropsWithChildren, useCallback, useRef } from 'react';
import { CProviderContext, CProviderContextValue } from './context';
import { getTranslations } from './translations';
import { CPortalLayer } from './portals';
import { CTooltipProvider } from '@/src/components/c-tooltip/CTooltip';
import { CNotificationsProvider } from '@/src/components/c-snackbar/CSnackbar';
import { CPopoverProvider } from '@/src/components/c-popover/CPopover';

export interface CProviderProps {
  locale?: string;
}

export function CProvider(props: PropsWithChildren<CProviderProps>): React.JSX.Element {
  const translations = getTranslations(props.locale);

  const selectRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const sidePanelRef = useRef<HTMLDivElement>(null);
  const bottomPanelRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const getPortalContainer = useCallback((layer: CPortalLayer): HTMLElement | null => {
    switch (layer) {
      case CPortalLayer.Select: return selectRef.current;
      case CPortalLayer.Tooltip: return tooltipRef.current;
      case CPortalLayer.Popover: return popoverRef.current;
      case CPortalLayer.Modal: return modalRef.current;
      case CPortalLayer.SidePanel: return sidePanelRef.current;
      case CPortalLayer.BottomPanel: return bottomPanelRef.current;
      case CPortalLayer.Notifications: return notificationsRef.current;
    }
  }, []);

  const value: CProviderContextValue = { translations, getPortalContainer };

  return (
    <CProviderContext.Provider value={value}>
      <CTooltipProvider>
        <CPopoverProvider>
          <CNotificationsProvider>
            {props.children}
          </CNotificationsProvider>
        </CPopoverProvider>
      </CTooltipProvider>
      <div ref={selectRef} id={CPortalLayer.Select} className="c-portal c-portal--select" />
      <div ref={tooltipRef} id={CPortalLayer.Tooltip} className="c-portal c-portal--tooltip" />
      <div ref={popoverRef} id={CPortalLayer.Popover} className="c-portal c-portal--popover" />
      <div ref={modalRef} id={CPortalLayer.Modal} className="c-portal c-portal--modal" />
      <div ref={sidePanelRef} id={CPortalLayer.SidePanel} className="c-portal c-portal--side-panel" />
      <div ref={bottomPanelRef} id={CPortalLayer.BottomPanel} className="c-portal c-portal--bottom-panel" />
      <div ref={notificationsRef} id={CPortalLayer.Notifications} className="c-portal c-portal--notifications" />
    </CProviderContext.Provider>
  );
}
